import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Select } from '@/nova/components';

import { preferredProfileId } from '../../html5-banners';
import type { PlayableExportFile, PlayableProfile } from '../../playable-api';
import * as S from './PlayableComposer.styles';

/** The last export of this session: which profile, its per-format files and the ZIP of all of them. */
export interface PlayableExportOutcome {
  profile: string;
  files: PlayableExportFile[];
  zip_url: string;
}

export interface ExportMenuProps {
  /** The environment's export profiles (empty until the HTML5 packager is deployed). */
  profiles: PlayableProfile[];
  /** A generated version is selected, so there is something to package. */
  ready: boolean;
  /** An export is being prepared. */
  busy: boolean;
  /** Another package of this version (a hosted embed) is being prepared: Export waits for it. */
  blocked?: boolean;
  /** The profile selected until the owner picks another (when the environment offers it); default: the first. */
  defaultProfile?: string;
  onExport: (profileId: string) => void;
}

/** The profile groups in the order the picker lists them (unknown groups go last, together). */
const GROUP_ORDER = ['web', 'ad-servers', 'in-app'];

/** Profiles ordered by group, known groups first (each group keeps the registry's order). Pure. */
export const orderProfiles = (profiles: readonly PlayableProfile[]): PlayableProfile[] => {
  const rank = (p: PlayableProfile): number => {
    const i = GROUP_ORDER.indexOf(p.group);
    return i < 0 ? GROUP_ORDER.length : i;
  };
  return profiles
    .map((p, i) => ({ p, i }))
    .sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i)
    .map(({ p }) => p);
};

/** A file's weight, in KB below 1 MB and MB above ('' when unknown). Pure. */
export const fileSizeText = (bytes: number | undefined): string =>
  bytes === undefined
    ? ''
    : bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * The export control, pinned to the output's foot: where the ad runs (the delivery profiles, Web first, then Ad
 * servers and In-app; each one packages the formats the way its channel accepts: single HTML or ZIP, its click-tag,
 * its weight budget) and one Export that downloads a ZIP of every format.
 */
export const ExportMenu = ({
  profiles,
  ready,
  busy,
  blocked = false,
  defaultProfile,
  onExport,
}: ExportMenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const [picked, setPicked] = React.useState<string>('');
  const profileId = profiles.some((p) => p.id === picked)
    ? picked
    : preferredProfileId(profiles, defaultProfile ? [defaultProfile] : []);

  if (profiles.length === 0) return <S.Hint text="Export profiles are not available in this environment yet." />;
  return (
    <>
      <S.ExportPick>
        <Select
          label={t('Where it runs')}
          hideLabel
          value={profileId}
          onChange={(id) => {
            if (!busy) setPicked(id);
          }}
          options={orderProfiles(profiles).map((p) => ({ value: p.id, label: p.label }))}
          disabled={busy}
        />
      </S.ExportPick>
      <Button
        variant="primary"
        icon="download"
        label={busy ? t('Preparing…') : t('Export ZIP')}
        disabled={!ready || busy || blocked || !profileId}
        onClick={() => {
          if (ready && !busy && !blocked && profileId) onExport(profileId);
        }}
      />
    </>
  );
};

export interface ExportOutcomeProps {
  /** The environment's export profiles (the outcome names its profile by label). */
  profiles: PlayableProfile[];
  outcome: PlayableExportOutcome;
  onDownload: (zipUrl: string) => void;
}

/** The last export of this session: its ZIP again, and per format whether it fits the channel's budget or which
 *  rules it breaks (never silently). */
export const ExportOutcome = ({ profiles, outcome, onDownload }: ExportOutcomeProps): React.ReactElement => {
  const { t } = useTranslation();
  const label = profiles.find((p) => p.id === outcome.profile)?.label ?? outcome.profile;
  return (
    <S.ExportPanel aria-label={t('Export')}>
      <S.FileHead>
        <S.FileMeta text="Last export · {{profile}}" placeholders={{ profile: label }} />
        {outcome.zip_url ? (
          <Button variant="text" icon="download" label={t('Download ZIP')} onClick={() => onDownload(outcome.zip_url)} />
        ) : null}
      </S.FileHead>
      <S.FileList>
        {outcome.files.map((file) => (
          <S.FileRow key={file.format}>
            <S.FileHead>
              <S.FileName raw text={file.format === 'responsive' ? t('Responsive') : file.format.replace('x', '×')} />
              {file.bytes !== undefined ? <S.FileMeta raw text={fileSizeText(file.bytes)} /> : null}
              {file.error ? (
                <S.BudgetOver text="Couldn't be packaged" />
              ) : file.within_budget === false ? (
                <S.BudgetOver text="Breaks the channel's rules" />
              ) : (
                <S.BudgetOk text="Within budget" />
              )}
            </S.FileHead>
            {file.error ? <S.Violation raw text={file.error} /> : null}
            {(file.violations ?? []).map((v) => (
              <S.Violation key={v} raw text={`· ${v}`} />
            ))}
          </S.FileRow>
        ))}
      </S.FileList>
    </S.ExportPanel>
  );
};

export default ExportMenu;
